import { Service } from "../types";
import {
  V2ProjectServiceResource,
  V2ProjectSubserviceResource,
} from "@generated";

interface Resource {
  data: { id: string; type: "project-resources" };
}

export const getResourceName = (resource) =>
  resource?.attributes?.["extended-name"] || resource?.attributes?.name || "";

export const formatResource = (service): Resource | undefined => {
  if (!service?.resource?.id || service?.resource?.id === -1) return;
  const resourceId = service?.resource?.id?.split("-").pop();
  const resourceType = service?.resource?.id?.split("-").slice(0, -1).join("-");

  return {
    data: {
      id:
        resourceType === "project-resources"
          ? resourceId
          : String(service?.resource?.id),
      type: "project-resources",
    },
  };
};

export interface ApplyFailure {
  serviceId: string;
  name: string;
  message: string;
  // True when the service itself was added and only a follow-up step failed,
  // so it must not be applied again.
  applied: boolean;
}

// The API's own explanation, so the user can see what to correct. `err` is
// the error an RTK Query trigger rejects with: `{ status, data }` for an HTTP
// failure, `{ status: "FETCH_ERROR", error }` when the request never landed.
export const apiErrorMessage = (err: any): string => {
  const errors = err?.data?.errors;
  if (Array.isArray(errors) && errors.length) {
    return errors
      .map((e) => e?.detail || e?.title)
      .filter(Boolean)
      .join("; ");
  }
  if (typeof err?.status === "number") {
    return `The server returned an error (${err.status}).`;
  }
  return err?.error || err?.message || "Something went wrong.";
};

type ApiTrigger = (arg: any) => { unwrap: () => Promise<any> };

// The generated v2 triggers the caller gets from its hooks.
export interface ProjectServicesApi {
  createProjectService: ApiTrigger;
  getProjectService: ApiTrigger;
  updateProjectSubservice: ApiTrigger;
}

// Applies each service independently and reports every failure rather than
// stopping at the first, so the caller can keep the user on the page with
// the reasons listed and drop the services that were added.
export const handleCreateProjectServices = async (
  services: Service[],
  serviceType: "professional_services" | "managed_services",
  project_id: number,
  api: ProjectServicesApi
): Promise<{ appliedIds: string[]; failures: ApplyFailure[] }> => {
  const appliedIds: string[] = [];
  const failures: ApplyFailure[] = [];

  for (const service of services) {
    const serviceId = String(service.id);
    const name = service?.name || "";
    const data: V2ProjectServiceResource = {
      type: "project-services",
      //@ts-ignore
      attributes: {
        name,
        quantity: Number(service?.quantity || 1),
        "override-hours": Number(service?.unitHours || 0),
        "service-type": serviceType,
      },
      relationships: {
        project: { data: { id: String(project_id), type: "projects" } },
        "project-resource": formatResource(service),
        service: {
          data: { id: String(service.id), type: "services" },
        },
      },
    };

    let projectServiceId;
    try {
      const serviceResponse = await api
        .createProjectService({ body: { data } })
        .unwrap();
      projectServiceId = serviceResponse?.data?.id;
    } catch (err) {
      failures.push({
        serviceId,
        name,
        message: apiErrorMessage(err),
        applied: false,
      });
      continue;
    }
    appliedIds.push(serviceId);

    const changedSubservices = (service.subservices || []).filter(
      (sub) => sub.isChanged
    );
    if (!projectServiceId || !changedSubservices.length) continue;

    try {
      const projectService = await api
        .getProjectService({
          id: String(projectServiceId),
          include: [
            "project-subservices",
            "project-subservices.subservice",
            "service",
            "service.subservices",
          ],
        })
        .unwrap();

      ///PATCH project-subservices with changed subservice data
      for (const subservice of changedSubservices) {
        const projectSubservice = projectService?.included?.find(
          (item) =>
            item?.type === "project-subservices" &&
            item?.relationships?.subservice?.data?.id === subservice.id
        );
        if (!projectSubservice) continue;

        const subData: V2ProjectSubserviceResource = {
          id: String(projectSubservice.id),
          type: "project-subservices",
          //@ts-ignore
          attributes: {
            name: subservice?.name || "",
            quantity: Number(subservice?.quantity || 1),
          },
          relationships: {
            "project-resource": formatResource(subservice),
            "project-service": {
              data: {
                id: projectServiceId,
                type: "project-services",
              },
            },
          },
        };

        try {
          await api
            .updateProjectSubservice({
              id: String(projectSubservice.id),
              body: { data: subData },
            })
            .unwrap();
        } catch (err) {
          failures.push({
            serviceId,
            name,
            message: `Subservice ${subservice?.name || ""}: ${apiErrorMessage(
              err
            )}`,
            applied: true,
          });
        }
      }
    } catch (err) {
      failures.push({
        serviceId,
        name,
        message: `Subservice changes: ${apiErrorMessage(err)}`,
        applied: true,
      });
    }
  }

  return { appliedIds, failures };
};
